/* eslint-disable camelcase */
import getConfig from "next/config";
import { AxiosRequestConfig, Method } from "axios";
import { JobSearchHit, JobSearchRequest, JobSearchResults } from "@themuse/design-system";
import { SearchResultsState } from "interfaces/context/searchResultsContextInterfaces";
import { isBrowser } from "../utils/browser";
import { jobSearchQueryMaxlength } from "../utils/constants";

/**
 * Formatter for The Muse Jobs.
 * This is the default.
 */

const getFormattedQueryParams = (queryParams: JobSearchRequest = null): any => ({
    ...queryParams,
    ...(queryParams.query && { query: queryParams.query.substring(0, jobSearchQueryMaxlength) })
});

/**
 * Returns the Axios config for requesting Jobs from TheMuse.
 */
const getMuseJobsConfig = (
    queryParams: JobSearchRequest,
    ctsEnabled: boolean,
    cookies?: string
): AxiosRequestConfig => {
    const { serverRuntimeConfig: { jobsApiHost } } = getConfig();
    const { publicRuntimeConfig: { apiBase, apiRequestTimeoutMs } } = getConfig();
    const searchVersion = ctsEnabled ? "cts_search" : "search";
    const path = isBrowser()
        ? `${apiBase}/jobs?ctsEnabled=${ctsEnabled}` : `${jobsApiHost}/api/v2/${searchVersion}/jobs`;
    const formattedQueryParams = getFormattedQueryParams(queryParams);

    const museConfig: AxiosRequestConfig = {
        method: ("GET" as Method),
        url: path,
        headers: isBrowser() ? {} : {
            Cookie: cookies || ""
        },
        params: {
            ...formattedQueryParams,
            timeout: apiRequestTimeoutMs
        }
    };

    return museConfig;
};

/**
 * This overwrite of company is only so we can remove the unused props that are in JobSearchResults.
 */
export interface Company {
    legacy_id: number;
    logo: string;
    name: string;
    short_name: string;
    client_tier?: string;
    perks_and_benefits?: Record<string, any>[];
    leadership_attributes?: Record<string, any>[];
    package?: string;
}

const formatJobsResponse = (jobsResponse: JobSearchResults): SearchResultsState => {
    const { count = 0, hits = [] } = jobsResponse;

    const cleanHits = hits.map((x: JobSearchHit) => {
        const result = x;
        delete (result.hit.company as Company).perks_and_benefits;
        delete (result.hit.company as Company).leadership_attributes;
        delete (result.hit.company as Company).package;
        return result;
    });

    const results = [...cleanHits];

    return {
        totalCount: count,
        results,
        // set the outer cursor to the last one in the set.
        cursor: results[results.length - 1]?.cursor ?? ""
    };
};

export { formatJobsResponse, getMuseJobsConfig };
